import { clsx } from "clsx";
import { useEffect, useState } from "react";
import { HexAlphaColorPicker } from "react-colorful";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import { Input } from "common/components/ui/Input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "common/components/ui/Popover";
import Skeleton from "common/components/ui/Skeleton";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useAgencySettings from "../../datahooks/useAgencySettings";
import useAgencySettingsMutations from "../../datahooks/useAgencySettingsMutations";
import SettingsItemContainer from "../SettingsItemContainer";

const colorPresets = [
  "#000000FF",
  "#9163FFFF",
  "#3067F2FF",
  "#25B972FF",
  "#FFC83FFF",
  "#FF8600FF",
  "#FF3350FF",
  "#E40069FF",
  "#FF0000FF",
  "#FD7E8CFF",
];

const defaultColor = "#000000FF";

export default function PrimaryColor() {
  const isDesktop = useTwBreakpoint("lg");
  const { agencySettings, isLoadingAgencySettings } = useAgencySettings();
  const { updateAgencySettings, isUpdatingAgencySettings } =
    useAgencySettingsMutations();

  const [color, setColor] = useState(
    agencySettings?.primary_color || defaultColor,
  );

  useEffect(() => {
    if (agencySettings?.primary_color) {
      setColor(agencySettings.primary_color);
    }
  }, [agencySettings]);

  function onCancel() {
    setColor(agencySettings?.primary_color || defaultColor);
  }

  return (
    <SettingsItemContainer className="flex-col gap-y-3">
      <span className="text-button-sm">Accent color</span>
      {isLoadingAgencySettings ? (
        <Skeleton className="h-8 w-48" />
      ) : (
        <div className="flex gap-x-2 items-center">
          <Popover>
            <PopoverTrigger
              className="border flex items-center justify-center size-11 lg:size-8 rounded-3xl lg:rounded-2xl"
              style={{ borderColor: color }}
            >
              <div
                className="size-8 rounded-xl lg:rounded-lg lg:size-6"
                style={{ backgroundColor: color }}
              />
            </PopoverTrigger>
            <PopoverContent
              className="flex flex-col p-3 gap-y-3 w-72"
              align="start"
            >
              <HexAlphaColorPicker
                color={color}
                onChange={setColor}
                className={clsx([
                  String.raw`!w-full gap-y-2 [&_.react-colorful\_\_saturation]:rounded-lg [&_.react-colorful\_\_saturation]:mb-1`,
                  String.raw`[&_.react-colorful\_\_alpha]:h-2 [&_.react-colorful\_\_alpha]:rounded-full`,
                  String.raw`[&_.react-colorful\_\_hue]:h-2 [&_.react-colorful\_\_hue]:rounded-full`,
                  String.raw`[&_.react-colorful\_\_pointer]:size-4 [&_.react-colorful\_\_pointer]:border-[3px]`,
                ])}
                data-vaul-no-drag // Prevent drawer from closing when dragging the Color Picker
              />
              <span className="text-button-xs">Preset colors</span>
              <div className="flex justify-between">
                {colorPresets.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className="size-4 rounded-md"
                    style={{ backgroundColor: color }}
                    onClick={() => setColor(color)}
                  />
                ))}
              </div>
            </PopoverContent>
          </Popover>
          <Input
            variant={isDesktop ? "sm" : "lg"}
            value={color.toUpperCase()}
            disabled={isUpdatingAgencySettings}
            onChange={(e) => setColor(e.target.value)}
          />
          <RenderIf
            condition={
              (agencySettings?.primary_color || defaultColor) !== color
            }
          >
            <div className="flex gap-x-2 mt-auto ml-auto">
              <Button
                variant="secondary-black"
                type="button"
                size="sm"
                onClick={onCancel}
                disabled={isUpdatingAgencySettings}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                type="submit"
                isLoading={isUpdatingAgencySettings}
                onClick={() =>
                  updateAgencySettings({
                    updates: { primary_color: color },
                  })
                }
              >
                Save changes
              </Button>
            </div>
          </RenderIf>
        </div>
      )}
    </SettingsItemContainer>
  );
}
